import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { DEFAULT_LOCALE, type Locale } from '@icebreaker/shared/domain';
import {
  LOCALE_STORAGE_KEY,
  createTranslator,
  isLocale,
  resolveLocale,
} from '@icebreaker/shared/i18n';
import { TranslatorProvider } from '@icebreaker/ui';
import { readPreserved, writePreserved } from '../storage/client-storage';
import { adminBrand } from '../config/env';

/**
 * TR + EN İLK GÜNDEN (CLAUDE.md). Sözlük `packages/shared`tedir — panel kendi
 * metin dosyasını AÇMAZ; bağlam da kittedir (`TranslatorProvider`). Burada
 * yalnız ÇEVİRMEN kurulur: marka katmanı ve `strict` kipi panelin kararıdır.
 *
 * Web'de dil yol önekinden (`/tr/...`) ve çerezden gelir çünkü SSR vardır.
 * Panelde SSR yoktur: seçim `localStorage`ta, anahtar ORTAK sözleşmeden
 * (`LOCALE_STORAGE_KEY`) alınır — iki uygulama aynı problemi iki adla çözmez.
 */
interface LocaleContextValue {
  readonly locale: Locale;
  readonly setLocale: (locale: Locale) => void;
}

const LocaleContext = createContext<LocaleContextValue | null>(null);

function initialLocale(): Locale {
  const stored = readPreserved(LOCALE_STORAGE_KEY);
  if (isLocale(stored)) return stored;
  if (typeof navigator === 'undefined') return DEFAULT_LOCALE;
  return resolveLocale(navigator.language);
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(initialLocale);

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
    writePreserved(LOCALE_STORAGE_KEY, next);
    document.documentElement.lang = next;
  }, []);

  const translator = useMemo(
    () =>
      createTranslator(locale, {
        brand: adminBrand,
        // Üretimde eksik anahtar ekrana anahtar adı yazmaz; CI tip düzeyinde yakalar.
        strict: process.env.NODE_ENV !== 'production',
      }),
    [locale],
  );

  const value = useMemo<LocaleContextValue>(() => ({ locale, setLocale }), [locale, setLocale]);

  return (
    <LocaleContext.Provider value={value}>
      <TranslatorProvider translator={translator}>{children}</TranslatorProvider>
    </LocaleContext.Provider>
  );
}

export function useLocale(): LocaleContextValue {
  const value = useContext(LocaleContext);
  if (!value) throw new Error('useLocale must be used within I18nProvider');
  return value;
}

/** Bileşenlerde tek metin kaynağı. Koda gömülü metin eslint ile yasaktır. */
export { useTranslation } from '@icebreaker/ui';
